﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateShopRequest,
  MetaCapiSettings,
  Shop,
  UpdateMetaCapiRequest,
  UpdateShopRequest,
  UpdateShopTrackingRequest,
} from "@ticketnjoy/types";

import { meQueryKey } from "@/features/organizers/api";
import { apiClient } from "@/lib/api-client";

export const shopsQueryKey = ["shops"] as const;
export const shopQueryKey = (slug: string) => ["shops", slug] as const;

export function useShopsQuery() {
  return useQuery<Shop[]>({
    queryKey: shopsQueryKey,
    queryFn: () => apiClient<Shop[]>("/shops"),
  });
}

export function useShopQuery(slug: string) {
  return useQuery<Shop>({
    queryKey: shopQueryKey(slug),
    queryFn: () => apiClient<Shop>(`/shops/${slug}`),
    enabled: Boolean(slug),
  });
}

/**
 * Shop list/detail ve /me cache'ini taze tutmak için tüm mutation'larda ortak invalidate.
 * /me içinde de shops array'i var, güncel kalsın.
 */
function invalidateShopCaches(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: shopsQueryKey });
  qc.invalidateQueries({ queryKey: meQueryKey });
}

export function useCreateShopMutation() {
  const qc = useQueryClient();
  return useMutation<Shop, Error, CreateShopRequest>({
    mutationFn: (body) =>
      apiClient<Shop>("/shops", { method: "POST", body }),
    onSuccess: (shop) => {
      qc.setQueryData(shopQueryKey(shop.slug), shop);
      invalidateShopCaches(qc);
    },
  });
}

export function useUpdateShopMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<Shop, Error, UpdateShopRequest>({
    mutationFn: (body) =>
      apiClient<Shop>(`/shops/${slug}`, { method: "PUT", body }),
    onSuccess: (shop) => {
      qc.setQueryData(shopQueryKey(shop.slug), shop);
      invalidateShopCaches(qc);
    },
  });
}

export function useUpdateShopTrackingMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<Shop, Error, UpdateShopTrackingRequest>({
    mutationFn: (body) =>
      apiClient<Shop>(`/shops/${slug}/tracking`, { method: "PUT", body }),
    onSuccess: (shop) => {
      qc.setQueryData(shopQueryKey(shop.slug), shop);
      invalidateShopCaches(qc);
    },
  });
}

/** `["shops", ...]` altında: izleme kaydı (pixel değişince anahtar silinir) bunu da tazeler. */
const metaCapiQueryKey = (slug: string) => ["shops", slug, "meta-capi"] as const;

export function useMetaCapiQuery(slug: string, enabled: boolean) {
  return useQuery<MetaCapiSettings>({
    queryKey: metaCapiQueryKey(slug),
    queryFn: () => apiClient<MetaCapiSettings>(`/shops/${slug}/tracking/meta-capi`),
    enabled: Boolean(slug) && enabled,
  });
}

export function useUpdateMetaCapiMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<MetaCapiSettings, Error, UpdateMetaCapiRequest>({
    mutationFn: (body) =>
      apiClient<MetaCapiSettings>(`/shops/${slug}/tracking/meta-capi`, { method: "PUT", body }),
    onSuccess: (settings) => qc.setQueryData(metaCapiQueryKey(slug), settings),
  });
}

export function useDeleteMetaCapiMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<MetaCapiSettings, Error, void>({
    mutationFn: () =>
      apiClient<MetaCapiSettings>(`/shops/${slug}/tracking/meta-capi`, { method: "DELETE" }),
    onSuccess: (settings) => qc.setQueryData(metaCapiQueryKey(slug), settings),
  });
}

type StatusAction = "pause" | "activate" | "archive";

export function useShopStatusMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<Shop, Error, StatusAction>({
    mutationFn: (action) =>
      apiClient<Shop>(`/shops/${slug}/${action}`, { method: "POST" }),
    onSuccess: (shop) => {
      qc.setQueryData(shopQueryKey(shop.slug), shop);
      invalidateShopCaches(qc);
    },
  });
}
